import { VictoryLabel, VictoryLine } from "victory";
import { CSSProperties } from "react";
import { FeedCurve } from "../../../../Services";
import { colors, fonts } from "../../../../Styles";
import { GraphTypes, useFeedCurveContext } from "../../../FeedCurves/_FeedCurves/feedCurveContext";

interface MilestoneGraphProps {
  feedCurve: FeedCurve,
  milestonesFirstDay: number[],
  extendedMaxAmount: number,
  firstDay: number,
  lastDay: number,
  overrides: {
    width: number,
    fontSize: CSSProperties,
    invertedColors: boolean
  }
}

const getMilestoneLines = ({ feedCurve, milestonesFirstDay, extendedMaxAmount, firstDay, lastDay, overrides }: MilestoneGraphProps): JSX.Element[] | null => {
  if (!feedCurve.milestones?.length) return null;

  const chartWidth = overrides.width;
  const dayRange = lastDay - firstDay;

  const _invertedColor = overrides.invertedColors ? colors.greyLightTertiary : colors.blackPrimary;
  const _invertedHoverColor = overrides.invertedColors ? colors.whitePrimary : colors.greyTertiary;

  const lines = feedCurve.milestones.map((milestone, index) => {
    const context = useFeedCurveContext();  
    const selectedMilestone = context?.state?.milestone?.selectedMilestone;
    const graphType = context?.state?.graphType;
    const dispatch = context?.dispatch;

    const firstMilestoneDay = milestonesFirstDay[index];
    const _extraHeight = extendedMaxAmount * 1.08;

    const _isHovered = ((graphType === GraphTypes.feedCurveWithLabels) && (selectedMilestone&&selectedMilestone === milestone)) ?? false
    
    // #region | Milestone Label Placement
    const currentX = ((firstMilestoneDay - firstDay) / dayRange) * chartWidth;

    let dy = -6;

    let nearbyCount = 0;
    for (let j = 0; j < index; j++) {
      const prevDay = milestonesFirstDay[j];
      const prevX = ((prevDay - firstDay) / dayRange) * chartWidth;
      const distancePx = currentX - prevX;
    
      if (distancePx < 20) {
        nearbyCount++;
      }
    }
    
    if (nearbyCount > 0) {
      dy += (nearbyCount % 2 === 0 ? 0 : 20 * (index-1));
    }
    // #endregion

    return (
      <VictoryLine
        key={milestone.milestoneId}
        groupComponent={
          <g style={{ zIndex: _isHovered ? 10 : 1 }} />
        }
        data={[
          { x: firstMilestoneDay, y: 0 },
          { x: firstMilestoneDay, y: extendedMaxAmount, label: `M${index + 1}` },
          { x: firstMilestoneDay, y: _extraHeight, label: undefined }
        ]}
        domain={{
          x: [firstDay, lastDay + 1],
          y: [0, _extraHeight]
        }}
        style={{ data: {
          stroke: _isHovered ? _invertedHoverColor : _invertedColor,
          strokeWidth: _isHovered ? 2 : 1,
          strokeDasharray: _isHovered ? 0 : 2,
          transition: "0.1s ease",
          transform: index === 0 && _isHovered ? "translateX(1px)" : index === 0 ? "translateX(1px)" : "none",
        }}}
        
        scale={{ x: "linear", y: "linear" }}
        standalone={false}
        labels={({ datum, index: datumIndex }) => datumIndex === 1 ? datum.label : undefined}
        labelComponent={
          <VictoryLabel
            // dy={-6}
            dy={dy}
            dx={3}
            verticalAnchor="end" 
            textAnchor="start" 
            style={{
              ...fonts.header,
              ...fonts.fontSize5,
              fill: _isHovered ? _invertedHoverColor : _invertedColor,
              fontSize: _isHovered
              ? ((parseFloat(overrides.fontSize.fontSize as string) || 12) * 1.2)
              : (parseFloat(overrides.fontSize.fontSize as string) || 12),
              transition: "0.3s ease",
            }}
          />
        }

        events={[{
          target: "labels",
          eventHandlers: {
            onMouseEnter: () => dispatch({milestone: {selectedMilestone: milestone}}),
            onMouseLeave: () => dispatch({milestone: {selectedMilestone: undefined}})
          },
        }]}
      />
    );
  });

  return lines;
};

export default getMilestoneLines